// Pure, DOM-free utilities shared by the Plample site and the outreach tool.
// The Tailwind-specific cn() helper deliberately lives in the site's own
// lib/utils.ts, not here — this package must stay renderable in plain Node.

export function toSlug(s: string): string {
  return s
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

export const SITE_URL = "https://plample.com";

// The retailers we scrape offers from. Shown to users as the set of sellers we
// are willing to send them to.
export const TRUSTED_RETAILERS = [
  "Amazon",
  "Currys PC World",
  "John Lewis",
  "Lenovo",
  "Apple",
  "HP",
  "Dell",
] as const;

// The wiki copy carries inline [cite:<source_id>] markers that the page renders
// as footnotes. Anywhere that shows the raw prose has to strip them first.
// Markers usually sit before the punctuation ("...reader [cite:3]."), so closing
// the gap they leave behind matters or the text reads "...reader .".
export function stripCitations(text: string): string {
  return text
    .replace(/\[cite:[^\]]+\]/g, "")
    .replace(/\s+([.,;:!?])/g, "$1")
    .replace(/\s{2,}/g, " ")
    .trim();
}

// Amazon storefront and Associates tag, per country. Each Amazon locale is a
// separate Associates programme with its own tag (UK tags end -21, US -20), so
// a UK tag on amazon.com earns nothing and sends the user to the wrong store.
const AMAZON_DOMAIN: Record<string, string> = {
  gb: "www.amazon.co.uk",
  us: "www.amazon.com",
};
const DEFAULT_AMAZON_COUNTRY = "gb";

// The env vars only exist to override; each locale falls back to its live tag.
// An untagged link would still work and still point at the right storefront —
// we just earn nothing on it, which beats linking to the wrong store.
const AMAZON_TAG: Record<string, string> = {
  gb: process.env.NEXT_PUBLIC_AMAZON_TAG_UK ?? "plample05-21",
  us: process.env.NEXT_PUBLIC_AMAZON_TAG_US ?? "plample05-20",
};

function amazonCountry(country: string | undefined): string {
  const c = (country ?? "").toLowerCase();
  return c in AMAZON_DOMAIN ? c : DEFAULT_AMAZON_COUNTRY;
}

export function amazonDomain(country?: string): string {
  return AMAZON_DOMAIN[amazonCountry(country)];
}

/** Product URL for an ASIN on the storefront for `country`. */
export function amazonDpUrl(asin: string, country?: string): string {
  return `https://${amazonDomain(country)}/dp/${asin}`;
}

// Append our affiliate tag to an Amazon URL. Non-Amazon (or empty) URLs are
// returned untouched, and any existing tag is overwritten. The tag is chosen by
// the URL's own hostname rather than the caller's country, so a scraped
// amazon.co.uk seller link never picks up a US tag (and vice versa) even if the
// page it is shown on disagrees.
export function withAmazonTag(url: string | null | undefined, country?: string): string | null {
  if (!url) return null;
  try {
    const u = new URL(url, `https://${amazonDomain(country)}`);
    if (!/(^|\.)amazon\./i.test(u.hostname)) return url;

    const locale = u.hostname.endsWith(".com") ? "us" : u.hostname.endsWith(".co.uk") ? "gb" : null;
    const tag = locale ? AMAZON_TAG[locale] : AMAZON_TAG[amazonCountry(country)];
    if (!tag) return u.toString();

    u.searchParams.set("tag", tag);
    return u.toString();
  } catch {
    return url;
  }
}
